/**
 * #627 — `/apps/:slug/about`, the [App name] menu's About: which platform this is (name and slug from
 * /api/meta), the app (name, registry record, owner, what it runs on, from GET /api/apps), the
 * serving version (GET /api/version), and where to read more.
 */
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '@/api/client';
import { useMeta } from '@/app/meta';
import { resolveConceptGuideHref } from '@/app/shell/concept-guide';
import { EmptyState } from '@/components/EmptyState';
import { Button, ButtonLink, Card } from '@/kit';
import { ErrorState, LoadingRows } from '@/features/governance/ui/primitives';
import { type PublishedApp, useApps } from './apps-api';

export interface ServingVersion {
  sha: string | null;
  imageDigest: string | null;
  migrations: number;
}

export function useServingVersion() {
  return useQuery<ServingVersion>({ queryKey: ['cigs', 'version'], queryFn: () => apiFetch<ServingVersion>('/version'), staleTime: 5 * 60_000 });
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-foreground">{children}</dd>
    </>
  );
}

export function AppAboutView({
  platform,
  app,
  version,
  onConceptGuide,
}: {
  platform: { name: string; slug: string };
  app: PublishedApp;
  version: ServingVersion | null;
  onConceptGuide?: () => void;
}) {
  return (
    <div className="space-y-4" data-testid="app-about">
      <header>
        <p className="text-[length:var(--t-th)] font-semibold uppercase tracking-wide text-muted-foreground">{platform.name}</p>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">About {app.name}</h1>
        {app.description ? <p className="mt-1 text-sm text-muted-foreground">{app.description}</p> : null}
      </header>
      <Card className="p-4">
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-2 text-sm">
          <Row label="Platform">
            {platform.name} <span className="id-mono text-[length:var(--t-th)] text-muted-foreground">({platform.slug})</span>
          </Row>
          <Row label="App">
            {app.name} <span className="id-mono text-[length:var(--t-th)] text-muted-foreground">{app.record.key}</span>
          </Row>
          <Row label="Owner">{app.owner?.name ?? 'No owner recorded'}</Row>
          <Row label="Runs on">{app.runsOn ? `${app.runsOn.name} (${app.runsOn.key})` : 'Not recorded'}</Row>
          <Row label="Serving version">
            {version ? (
              <span className="whitespace-normal font-mono text-[length:var(--t-th)]">
                {version.sha ? version.sha.slice(0, 12) : 'local build (no release SHA)'} · {version.migrations} migrations
                {version.imageDigest ? <span className="block whitespace-normal break-all text-muted-foreground">{version.imageDigest}</span> : null}
              </span>
            ) : (
              'Not available'
            )}
          </Row>
        </dl>
      </Card>
      <div className="flex flex-wrap gap-2">
        <Button onClick={onConceptGuide}>
          Concept guide
        </Button>
        <ButtonLink to="/library?q=handoff" variant="secondary">
          Release handoff notes
        </ButtonLink>
      </div>
    </div>
  );
}

export default function AppAboutPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const meta = useMeta();
  const navigate = useNavigate();
  const apps = useApps();
  const version = useServingVersion();
  if (apps.isError) return <ErrorState className="m-6" error={apps.error} onRetry={() => void apps.refetch()} />;
  if (apps.isLoading || !apps.data) return <LoadingRows className="m-6" rows={4} />;
  const app = apps.data.apps.find((a) => a.slug === slug);
  if (!app) {
    return (
      <div className="px-6 py-5">
        <EmptyState icon={<Info className="h-5 w-5" aria-hidden />} title="No such app" description={`No app with the slug “${slug}” is published to you.`} action={{ label: 'Open Apps', onClick: () => navigate('/apps') }} />
      </div>
    );
  }
  const platform = { name: meta.platformName?.trim() || apps.data.platform.name || '', slug: meta.platformSlug?.trim() || apps.data.platform.slug || '' };
  return <AppAboutView platform={platform} app={app} version={version.data ?? null} onConceptGuide={() => void resolveConceptGuideHref().then((href) => navigate(href))} />;
}
